import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useOktaAuth } from '@okta/okta-react';
import * as analytics from '@benniehealth/core-js/analytics';
import postLoginExperience from '@benniehealth/core-js/store/postLoginExperience';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { track } from '@app/helpers/analytics';
import ContentLayout from '@app/containers/App/ContentLayout';
import Navigation from '@app/containers/Navigation';
import {
  setAskBennieAiVisibility,
  setAskBennieAiInitialMessage,
  setAskBennieModalVisibility,
} from '@app/store/modal';
import { NavigationContext } from '@app/contexts/NavigationContext';
import { getSteps } from '@app/helpers/userTutorial';
import { useTranslation } from '@app/i18n';
import useLDUser from '@app/hooks/useLDUser';
import useTourState from '@app/hooks/useTourState';
import navigationService from '@app/services/navigationService';
import store from '@app/configureStore';
import { lazyWithRetry } from '@app/utils/loadable';

const LazyAskBennieOptionsModal = lazyWithRetry(
  () => import('@app/components/AskBennieOptionsModal'),
);
const LazyAskBennieAiChatModal = lazyWithRetry(() =>
  import('@app/components/AskBennieAiChatModal').then(mod => ({
    default: mod.AskBennieAiChatModal,
  })),
);

const LazyUserTutorial = lazyWithRetry(() => {
  return import('@benniehealth/components-web/components/UserTutorial').then(
    mod => ({
      default: mod.UserTutorial || mod.default,
    }),
  );
});

const TUTORIAL_EVENTS = {
  NEXT: analytics.postLogin.TUTORIAL_NEXT,
  CLOSE: analytics.postLogin.TUTORIAL_CLOSE,
  DONE: analytics.postLogin.TUTORIAL_DONE,
};

const AppLayout = ({ actions }) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [hasBeenAuthenticated, setHasBeenAuthenticated] = useState(false);

  const dispatch = useDispatch();
  const aiOpen = useSelector(state => state.modal.askBennieAiIsVisible);
  const pendingInitialMessage = useSelector(
    state => state.modal.askBennieAiInitialMessage,
  );
  const { isLdUserIdentified } = useLDUser();
  const { askBennieAiWeb } = useFlags();
  const hasAiFlag = Boolean(isLdUserIdentified && askBennieAiWeb);
  const { shouldRunAppIntroTour } = useTourState();
  const runTour = Boolean(shouldRunAppIntroTour && isLdUserIdentified);
  const { authState } = useOktaAuth();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const STEPS = getSteps(!mdUp);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      setHasBeenAuthenticated(true);
    }
  }, [authState?.isAuthenticated]);

  useEffect(() => {
    navigationService.setDispatch(store.dispatch);
  }, []);

  const onTutorialChange = event => data => {
    if ([TUTORIAL_EVENTS.CLOSE, TUTORIAL_EVENTS.DONE].indexOf(event) !== -1) {
      actions.onCompleteAppTutorial();
    }

    track(analytics.common.getEventType(event), {
      title: data.step.title,
    });
  };

  const onToggleMenu = useCallback(() => setIsNavOpen(prev => !prev), []);

  const navigationContextValue = useMemo(
    () => ({
      isNavOpen,
      setIsNavOpen,
      onToggleMenu,
    }),
    [isNavOpen, onToggleMenu],
  );

  return (
    <NavigationContext.Provider value={navigationContextValue}>
      {hasBeenAuthenticated && hasAiFlag && (
        <Suspense fallback={null}>
          <LazyAskBennieAiChatModal
            open={aiOpen && authState?.isAuthenticated}
            onClose={() => {
              dispatch(setAskBennieAiVisibility({ isVisible: false }));
              dispatch(setAskBennieAiInitialMessage({ initialMessage: null }));
            }}
            initialMessage={pendingInitialMessage}
            onOpenLegacy={() => {
              dispatch(setAskBennieAiVisibility({ isVisible: false }));
              dispatch(setAskBennieAiInitialMessage({ initialMessage: null }));
              dispatch(setAskBennieModalVisibility({ isVisible: true }));
            }}
          />
        </Suspense>
      )}
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100vh"
        position={runTour ? 'fixed' : 'inherit'}
        width="100%"
      >
        <Box display="flex" flexDirection="column" flex={1} position="relative">
          <Navigation
            key="main-navigation"
            isOpen={isNavOpen}
            onClose={onToggleMenu}
            onOpen={onToggleMenu}
          />
          {runTour && (
            <Suspense fallback={null}>
              <LazyUserTutorial
                run={runTour}
                steps={STEPS}
                nextText={t(`common.next`)}
                lastStepText={t(`common.done`)}
                onExit={onTutorialChange(TUTORIAL_EVENTS.CLOSE)}
                onNext={onTutorialChange(TUTORIAL_EVENTS.NEXT)}
                onComplete={onTutorialChange(TUTORIAL_EVENTS.DONE)}
              />
            </Suspense>
          )}
          <Suspense fallback={null}>
            <LazyAskBennieOptionsModal />
          </Suspense>
          <ContentLayout />
        </Box>
      </Box>
    </NavigationContext.Provider>
  );
};

AppLayout.propTypes = {
  actions: PropTypes.shape({
    onCompleteAppTutorial: PropTypes.func,
  }),
};

const mapDispatchToProps = dispatch => {
  return {
    actions: {
      onCompleteAppTutorial: () =>
        dispatch(postLoginExperience.actions.completeAppTutorialRequest()),
    },
  };
};

export default connect(null, mapDispatchToProps)(AppLayout);
